/**
 * How this phone prints: straight to a paired Bluetooth printer, or
 * through the browser's print dialog (any printer Android knows about,
 * and the fallback when Bluetooth fails). Remembered on the phone.
 */
import * as db from './db'
import { bluetoothAvailable, pairPrinter, send } from './bluetooth'
import { DOTS, type Paper } from './escpos'
import { type Block, draw, printerBytes } from './ticket'
import { printHtml } from '../inventory/receipt'

export interface PrinterSettings {
  mode: 'bluetooth' | 'dialog'
  id: string | null
  name: string | null
  paper: Paper
}

export const DEFAULT_PRINTER: PrinterSettings = { mode: 'dialog', id: null, name: null, paper: 80 }

export async function loadPrinter(): Promise<PrinterSettings> {
  return { ...DEFAULT_PRINTER, ...((await db.get<PrinterSettings>('printer')) ?? {}) }
}

export async function savePrinter(p: PrinterSettings): Promise<void> {
  await db.set('printer', p)
}

export async function pair(p: PrinterSettings): Promise<PrinterSettings> {
  const dev = await pairPrinter()
  const next = { ...p, mode: 'bluetooth' as const, id: dev.id, name: dev.name ?? 'Printer' }
  await savePrinter(next)
  return next
}

/** The ticket as a picture, printed through the browser's dialog. */
export async function printViaDialog(blocks: Block[], paper: Paper): Promise<void> {
  const canvas = await draw(blocks, DOTS[paper])
  const mm = paper
  printHtml(`<!doctype html><html><head><meta charset="utf-8"><style>
    @page { size: ${mm}mm auto; margin: 2mm; } body { margin: 0; }
    img { width: ${mm - 6}mm; display: block; margin: 0 auto; }
  </style></head><body><img src="${canvas.toDataURL('image/png')}"></body></html>`)
}

/** Print the blocks the way this phone prints. Throws 'PAIR' when the
 *  Bluetooth printer has to be picked again (after the browser restarted). */
export async function printBlocks(blocks: Block[], p: PrinterSettings): Promise<void> {
  if (p.mode === 'bluetooth' && bluetoothAvailable()) {
    await send(await printerBytes(blocks, p.paper), p.id)
    return
  }
  await printViaDialog(blocks, p.paper)
}
